/* Kortingscodes management page in the shared admin page frame. */



import { useState, useEffect } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, CreditCard as Edit2, Trash2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Tag, Copy, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Coupon {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_amount: number | null;
  usage_limit: number | null;
  usage_count: number;
  valid_from: string;
  valid_until: string | null;
  active: boolean;
  created_at: string;
}

export default function KortingscodesPage() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 0,
    min_order_amount: '',
    usage_limit: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    active: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('coupon_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCoupons(data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setMessage({ type: 'error', text: 'Fout bij het ophalen van kortingscodes' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (coupon?: Coupon) => {
    if (coupon) {
      setSelectedCoupon(coupon);
      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_order_amount: coupon.min_order_amount?.toString() || '',
        usage_limit: coupon.usage_limit?.toString() || '',
        valid_from: coupon.valid_from.split('T')[0],
        valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
        active: coupon.active
      });
    } else {
      setSelectedCoupon(null);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        min_order_amount: '',
        usage_limit: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: '',
        active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedCoupon(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const couponData = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        valid_from: formData.valid_from,
        valid_until: formData.valid_until || null,
        active: formData.active
      };

      if (selectedCoupon) {
        const { error } = await supabase
          .from('coupon_codes')
          .update(couponData)
          .eq('id', selectedCoupon.id);

        if (error) throw error;
        setMessage({ type: 'success', text: 'Kortingscode succesvol bijgewerkt' });
      } else {
        const { error } = await supabase
          .from('coupon_codes')
          .insert([{ ...couponData, usage_count: 0 }]);

        if (error) throw error;
        setMessage({ type: 'success', text: 'Kortingscode succesvol toegevoegd' });
      }

      handleCloseDialog();
      fetchCoupons();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving coupon:', error);
      setMessage({ type: 'error', text: error.message || 'Fout bij het opslaan van kortingscode' });
    }
  };

  const handleDelete = async () => {
    if (!couponToDelete) return;

    try {
      const { error } = await supabase
        .from('coupon_codes')
        .delete()
        .eq('id', couponToDelete.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Kortingscode succesvol verwijderd' });
      setIsDeleteDialogOpen(false);
      setCouponToDelete(null);
      fetchCoupons();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setMessage({ type: 'error', text: 'Fout bij het verwijderen van kortingscode' });
    }
  };

  const toggleActive = async (coupon: Coupon) => {
    try {
      const { error } = await supabase
        .from('coupon_codes')
        .update({ active: !coupon.active })
        .eq('id', coupon.id);

      if (error) throw error;
      fetchCoupons();
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      setMessage({ type: 'error', text: 'Fout bij het wijzigen van status' });
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setMessage({ type: 'success', text: 'Code gekopieerd naar klembord' });
    setTimeout(() => setMessage(null), 2000);
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}%`;
    }
    return `€${coupon.discount_value.toFixed(2)}`;
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Kortingscodes" />

        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Kortingscodes</h1>
            <p className="text-gray-600">Beheer kortingscodes en acties</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-jv-primary hover:bg-jv-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe kortingscode
          </Button>
        </div>

        {message && (
          <Alert className={`mb-6 ${message.type === 'success' ? 'bg-green-50 text-green-900 border-green-200' : 'bg-red-50 text-red-900 border-red-200'}`}>
            {message.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jv-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Laden...</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {coupons.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Tag className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Geen kortingscodes gevonden</p>
                  <Button onClick={() => handleOpenDialog()} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Eerste kortingscode toevoegen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              coupons.map((coupon) => (
                <Card key={coupon.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-[#f5eede] text-[#c8a94a] p-3 rounded-lg">
                          <Tag className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900">{coupon.code}</h3>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(coupon.code)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            {!coupon.active && (
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                Inactief
                              </span>
                            )}
                          </div>
                          <div className="flex gap-4 text-sm text-gray-600">
                            <span className="font-semibold text-jv-primary">{formatDiscount(coupon)}</span>
                            {coupon.min_order_amount && (
                              <span>Min. bestelwaarde: €{coupon.min_order_amount.toFixed(2)}</span>
                            )}
                            {coupon.usage_limit && (
                              <span>Gebruikt: {coupon.usage_count}/{coupon.usage_limit}</span>
                            )}
                            {coupon.valid_until && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Geldig tot: {new Date(coupon.valid_until).toLocaleDateString('nl-NL')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={coupon.active}
                          onCheckedChange={() => toggleActive(coupon)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(coupon)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCouponToDelete(coupon);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedCoupon ? 'Kortingscode bewerken' : 'Nieuwe kortingscode'}</DialogTitle>
              <DialogDescription>
                {selectedCoupon ? 'Wijzig de gegevens van de kortingscode' : 'Voeg een nieuwe kortingscode toe'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="ZOMER2024"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="discount_type">Type *</Label>
                    <Select
                      value={formData.discount_type}
                      onValueChange={(value: 'percentage' | 'fixed') => setFormData({ ...formData, discount_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Vast bedrag</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="discount_value">Waarde *</Label>
                    <Input
                      id="discount_value"
                      type="number"
                      step="0.01"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="min_order_amount">Min. bestelwaarde (optioneel)</Label>
                  <Input
                    id="min_order_amount"
                    type="number"
                    step="0.01"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="usage_limit">Max. aantal gebruiken (optioneel)</Label>
                  <Input
                    id="usage_limit"
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    placeholder="Onbeperkt"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valid_from">Geldig vanaf *</Label>
                    <Input
                      id="valid_from"
                      type="date"
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="valid_until">Geldig tot (optioneel)</Label>
                    <Input
                      id="valid_until"
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label htmlFor="active">Actief</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Annuleren
                </Button>
                <Button type="submit" className="bg-jv-primary hover:bg-jv-primary/90">
                  {selectedCoupon ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Kortingscode verwijderen</DialogTitle>
              <DialogDescription>
                Weet je zeker dat je de kortingscode "{couponToDelete?.code}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Annuleren
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
